const createDecoration = (type, x, y, width, height, color, extra = {}) => ({
  type,
  x,
  y,
  width,
  height,
  color,
  ...extra,
});

const createLandingLayout = (creatureX, villageX, landmarkX, gateX, playerX = 0.42) => ({
  creatureX,
  villageX,
  landmarkX,
  gateX,
  playerX,
});

const createVillageSpot = (
  name,
  type,
  dialogLines,
  widthScale = 0.88,
  heightScale = 0.82,
  reward = {}
) => ({
  name,
  type,
  dialogLines,
  widthScale,
  heightScale,
  reward: {
    points: reward.points ?? 8,
    label: reward.label ?? `${name} Glow`,
    prompt: reward.prompt ?? 'Tap to visit',
    activePrompt: reward.activePrompt ?? 'Glow is awake',
    reactionType: reward.reactionType ?? type,
    mechanicType: reward.mechanicType ?? reward.reactionType ?? type,
    mechanicLabel: reward.mechanicLabel ?? reward.label ?? `${name} Glow`,
    mechanicHint: reward.mechanicHint ?? '',
    activationLines: reward.activationLines ?? dialogLines,
    revisitLines: reward.revisitLines ?? dialogLines,
  },
});

export const BIOMES = [
  {
    id: 'lantern-bamboo-valley',
    name: 'Lantern Bamboo Valley',
    shortName: 'Bamboo Valley',
    regionLabel: 'Lantern-lit bamboo bridges and glowing mountain mist',
    goalLabel: 'Lantern Gate',
    palette: {
      skyTint: 'linear-gradient(180deg, rgba(14, 55, 78, 0.14) 0%, rgba(243, 173, 90, 0.28) 100%)',
      horizonGlow: 'rgba(255, 220, 157, 0.34)',
      panel: 'rgba(255, 247, 233, 0.8)',
      panelBorder: 'rgba(255, 255, 255, 0.72)',
      heading: '#173858',
      accent: '#f59e0b',
      accentSoft: '#fde68a',
      questItem: '#ffd166',
      questItemSoft: '#fff6cc',
      platformTint: 'rgba(104, 177, 142, 0.22)',
      platformGlow: 'rgba(253, 230, 138, 0.45)',
      leaf: '#9ae6b4',
      leafShadow: 'rgba(26, 102, 65, 0.25)',
      creature: '#f59e0b',
      creatureSecondary: '#fff4d6',
      companion: '#fbbf24',
      guide: '#fef3c7',
      dialog: '#fff7db',
    },
    creature: {
      id: 'lantern-fox',
      type: 'fox',
      name: 'Lantern Fox',
      greeting: 'Ni hao! Hello, Garden Messenger!',
      prompt: 'Could you gather 3 lantern hearts so the valley can glow tonight?',
      reminder: 'The hearts are twinkling on the higher bridges.',
      thanks: 'The valley shines again. Please carry my guiding light onward.',
    },
    quest: {
      id: 'lantern-seeds',
      itemType: 'seed',
      itemLabel: 'lantern heart',
      itemPlural: 'lantern hearts',
      total: 3,
      hudLabel: 'Glow Restored',
      collectVerb: 'Gather',
      completionText: 'Lanterns shimmer awake across the bamboo bridges.',
    },
    companion: {
      id: 'glow-fox',
      name: 'Glow Fox',
      ability: 'Reveals guiding lights on special platforms.',
      effect: 'guide-lights',
      badge: 'GF',
    },
    landing: {
      platformName: 'Lantern Landing',
      gateName: 'Sky Lantern Gate',
      landmarkName: 'Lantern Stand',
      layout: createLandingLayout(0.16, 0.38, 0.61, 0.84, 0.34),
      landmarkDialog: [
        'Lantern lights rest low',
        'kind hellos wake the sky pathway',
        'the fox keeps watch nearby',
      ],
      village: createVillageSpot(
        'Tea Table',
        'tea-table',
        [
          'Warm tea waits here',
          'small bridge talk drifts through the mist',
          'the lantern gate glows east',
        ],
        0.88,
        0.82,
        {
          label: 'Lantern Lights',
          prompt: 'Light the bridge',
          activePrompt: 'Lights are awake',
          reactionType: 'lantern-lights',
          mechanicType: 'wind-glide',
          mechanicLabel: 'Wind Glide',
          mechanicHint: 'Drift while falling',
          activationLines: [
            'Tea lights wake bright',
            'small bridge lanterns guide the climb',
            'you gained 8 harmony',
          ],
          revisitLines: [
            'Lantern lights stay warm',
            'the bridge path keeps its soft glow',
            'the gate shines ahead',
          ],
        }
      ),
    },
    decorations: {
      far: [
        createDecoration('hill', 0.08, 0.22, 0.36, 0.18, 'rgba(41, 95, 97, 0.26)'),
        createDecoration('hill', 0.52, 0.18, 0.42, 0.2, 'rgba(31, 79, 99, 0.22)'),
        createDecoration('orb', 0.72, 0.11, 0.1, 0.12, 'rgba(255, 221, 173, 0.36)'),
      ],
      mid: [
        createDecoration('bamboo', 0.12, 0.3, 0.12, 0.22, 'rgba(63, 155, 119, 0.34)'),
        createDecoration('lantern', 0.72, 0.28, 0.08, 0.14, 'rgba(255, 210, 118, 0.72)'),
        createDecoration('bridge', 0.28, 0.42, 0.24, 0.08, 'rgba(120, 88, 58, 0.34)'),
        createDecoration('bird', 0.62, 0.26, 0.12, 0.07, 'rgba(255, 255, 255, 0.44)'),
      ],
      fore: [
        createDecoration('grass', 0.08, 0.72, 0.18, 0.12, 'rgba(61, 153, 116, 0.34)'),
        createDecoration('grass', 0.74, 0.78, 0.18, 0.12, 'rgba(61, 153, 116, 0.34)'),
        createDecoration('lantern', 0.2, 0.66, 0.07, 0.12, 'rgba(255, 214, 122, 0.56)', {
          small: true,
        }),
      ],
    },
  },
  {
    id: 'highland-meadow',
    name: 'Highland Meadow',
    shortName: 'Highland Meadow',
    regionLabel: 'Mist hills, stone circles, and soft meadow wind',
    goalLabel: 'Heather Arch',
    palette: {
      skyTint: 'linear-gradient(180deg, rgba(73, 104, 144, 0.14) 0%, rgba(212, 241, 226, 0.26) 100%)',
      horizonGlow: 'rgba(220, 243, 235, 0.26)',
      panel: 'rgba(247, 252, 248, 0.8)',
      panelBorder: 'rgba(255, 255, 255, 0.72)',
      heading: '#1f3655',
      accent: '#4f9d7a',
      accentSoft: '#cfeee0',
      questItem: '#a7f3d0',
      questItemSoft: '#f0fdf8',
      platformTint: 'rgba(112, 169, 146, 0.22)',
      platformGlow: 'rgba(167, 243, 208, 0.42)',
      leaf: '#c6f6d5',
      leafShadow: 'rgba(29, 78, 56, 0.22)',
      creature: '#d9f2e6',
      creatureSecondary: '#fefefe',
      companion: '#7dd3ae',
      guide: '#d1fae5',
      dialog: '#eefcf4',
    },
    creature: {
      id: 'meadow-sheep',
      type: 'sheep',
      name: 'Highland Sheep',
      greeting: 'Hello, wee friend!',
      prompt: 'Could you collect 3 meadow songs drifting in the mist?',
      reminder: 'The songs sparkle above the stone rings.',
      thanks: 'The hills are humming again. Follow the gentle breeze.',
    },
    quest: {
      id: 'meadow-songs',
      itemType: 'song',
      itemLabel: 'meadow song',
      itemPlural: 'meadow songs',
      total: 3,
      hudLabel: 'Songs Gathered',
      collectVerb: 'Catch',
      completionText: 'The hillside breeze starts to sing along with you.',
    },
    companion: {
      id: 'wind-sheep',
      name: 'Wind Sheep',
      ability: 'Softens long falls with a meadow breeze.',
      effect: 'gentle-breeze',
      badge: 'WS',
    },
    landing: {
      platformName: 'Heather Landing',
      gateName: 'Breeze Arch',
      landmarkName: 'Stone Circle',
      layout: createLandingLayout(0.18, 0.56, 0.36, 0.82, 0.32),
      landmarkDialog: [
        'Old stones hum hello',
        'soft songs wake when friends feel heard',
        'the arch opens in wind',
      ],
      village: createVillageSpot(
        'Wool Cart',
        'wool-cart',
        [
          'Soft wool rests here',
          'hill folk trade songs by the path',
          'the breeze arch waits ahead',
        ],
        0.88,
        0.82,
        {
          label: 'Hill Breeze',
          prompt: 'Wake the breeze',
          activePrompt: 'Breeze is awake',
          reactionType: 'hill-breeze',
          mechanicType: 'stone-steady',
          mechanicLabel: 'Stone Steady',
          mechanicHint: 'Moving platforms hold',
          activationLines: [
            'Wool flags lift high',
            'a hill breeze clears the path',
            'you gained 8 harmony',
          ],
          revisitLines: [
            'The hill breeze stays',
            'soft cloth points toward the arch',
            'the gate waits ahead',
          ],
        }
      ),
    },
    decorations: {
      far: [
        createDecoration('hill', 0.02, 0.18, 0.48, 0.2, 'rgba(84, 123, 116, 0.22)'),
        createDecoration('hill', 0.52, 0.2, 0.42, 0.2, 'rgba(110, 145, 137, 0.18)'),
        createDecoration('orb', 0.18, 0.14, 0.08, 0.1, 'rgba(248, 255, 255, 0.22)'),
      ],
      mid: [
        createDecoration('ring', 0.14, 0.34, 0.16, 0.12, 'rgba(161, 178, 171, 0.52)'),
        createDecoration('sheep-cloud', 0.66, 0.3, 0.16, 0.1, 'rgba(255, 255, 255, 0.56)'),
        createDecoration('mist-arc', 0.42, 0.39, 0.28, 0.08, 'rgba(255, 255, 255, 0.22)'),
      ],
      fore: [
        createDecoration('grass', 0.12, 0.76, 0.22, 0.12, 'rgba(93, 148, 108, 0.3)'),
        createDecoration('grass', 0.68, 0.8, 0.22, 0.12, 'rgba(93, 148, 108, 0.3)'),
        createDecoration('ring', 0.8, 0.68, 0.1, 0.08, 'rgba(189, 206, 199, 0.36)'),
      ],
    },
  },
  {
    id: 'storybook-forest',
    name: 'Storybook Forest',
    shortName: 'Storybook Forest',
    regionLabel: 'Mushroom glades, paper trees, and twinkling pages',
    goalLabel: 'Story Arch',
    palette: {
      skyTint: 'linear-gradient(180deg, rgba(75, 52, 98, 0.12) 0%, rgba(171, 222, 184, 0.26) 100%)',
      horizonGlow: 'rgba(214, 245, 225, 0.28)',
      panel: 'rgba(249, 245, 238, 0.82)',
      panelBorder: 'rgba(255, 255, 255, 0.72)',
      heading: '#3f2a58',
      accent: '#8b5cf6',
      accentSoft: '#ede9fe',
      questItem: '#fcd34d',
      questItemSoft: '#fff7dd',
      platformTint: 'rgba(132, 184, 120, 0.24)',
      platformGlow: 'rgba(252, 211, 77, 0.42)',
      leaf: '#b6e3a6',
      leafShadow: 'rgba(59, 107, 63, 0.22)',
      creature: '#f7d488',
      creatureSecondary: '#f9f3e2',
      companion: '#c4b5fd',
      guide: '#fef3c7',
      dialog: '#fff7e6',
    },
    creature: {
      id: 'story-gnome',
      type: 'gnome',
      name: 'Story Gnome',
      greeting: 'Hallo, bright hoof!',
      prompt: 'Please collect 3 story stars so the forest can tell bedtime tales.',
      reminder: 'The stars are tucked between the mushroom ledges.',
      thanks: 'Every tree remembers your kindness. Take this garden bloom along.',
    },
    quest: {
      id: 'story-stars',
      itemType: 'star',
      itemLabel: 'story star',
      itemPlural: 'story stars',
      total: 3,
      hudLabel: 'Stars Found',
      collectVerb: 'Gather',
      completionText: 'Golden story stars swirl through the forest canopy.',
    },
    companion: {
      id: 'butterfly-spirit',
      name: 'Butterfly Spirit',
      ability: 'Grows wider rescue leaves beneath the unicorn.',
      effect: 'leaf-bloom',
      badge: 'BS',
    },
    landing: {
      platformName: 'Story Landing',
      gateName: 'Page Arch',
      landmarkName: 'Story Tree',
      layout: createLandingLayout(0.2, 0.42, 0.68, 0.86, 0.34),
      landmarkDialog: [
        'Paper leaves lean close',
        'little tales wake after kindness',
        'the page arch waits ahead',
      ],
      village: createVillageSpot(
        'Mushroom House',
        'mushroom-house',
        [
          'Round lamps glow here',
          'quiet tales curl under the cap',
          'the page arch waits right',
        ],
        0.96,
        0.92,
        {
          label: 'Story Glow',
          prompt: 'Wake the tale',
          activePrompt: 'Story glow is awake',
          reactionType: 'story-glow',
          mechanicType: 'leaf-bloom',
          mechanicLabel: 'Leaf Bloom',
          mechanicHint: 'Platforms grow wider',
          activationLines: [
            'Page lights drift out',
            'small tales wake beside the house',
            'you gained 8 harmony',
          ],
          revisitLines: [
            'Story lights still turn',
            'the page path glows by the cap',
            'the arch waits right',
          ],
        }
      ),
    },
    decorations: {
      far: [
        createDecoration('hill', 0.06, 0.19, 0.36, 0.18, 'rgba(84, 92, 118, 0.16)'),
        createDecoration('hill', 0.54, 0.22, 0.34, 0.18, 'rgba(69, 110, 84, 0.18)'),
        createDecoration('orb', 0.76, 0.13, 0.08, 0.1, 'rgba(255, 246, 211, 0.22)'),
      ],
      mid: [
        createDecoration('storybook-tree', 0.16, 0.32, 0.14, 0.22, 'rgba(120, 169, 111, 0.34)'),
        createDecoration('mushroom', 0.72, 0.42, 0.14, 0.14, 'rgba(240, 151, 107, 0.46)'),
        createDecoration('sparkle-cluster', 0.5, 0.28, 0.14, 0.08, 'rgba(255, 222, 129, 0.6)'),
      ],
      fore: [
        createDecoration('grass', 0.1, 0.8, 0.2, 0.12, 'rgba(113, 168, 99, 0.3)'),
        createDecoration('mushroom', 0.26, 0.74, 0.1, 0.1, 'rgba(239, 163, 118, 0.42)', {
          small: true,
        }),
        createDecoration('grass', 0.72, 0.82, 0.2, 0.12, 'rgba(113, 168, 99, 0.3)'),
      ],
    },
  },
  {
    id: 'sun-orchard',
    name: 'Sun Orchard',
    shortName: 'Sun Orchard',
    regionLabel: 'Warm arches, singing birds, and citrus light',
    goalLabel: 'Golden Arbor',
    palette: {
      skyTint: 'linear-gradient(180deg, rgba(255, 171, 74, 0.12) 0%, rgba(255, 224, 178, 0.26) 100%)',
      horizonGlow: 'rgba(255, 237, 198, 0.28)',
      panel: 'rgba(255, 250, 240, 0.82)',
      panelBorder: 'rgba(255, 255, 255, 0.72)',
      heading: '#7a3b16',
      accent: '#f97316',
      accentSoft: '#ffedd5',
      questItem: '#fb923c',
      questItemSoft: '#fff1df',
      platformTint: 'rgba(244, 170, 91, 0.22)',
      platformGlow: 'rgba(251, 146, 60, 0.38)',
      leaf: '#f9d89d',
      leafShadow: 'rgba(142, 85, 26, 0.2)',
      creature: '#ffd4a8',
      creatureSecondary: '#fff7ed',
      companion: '#fdba74',
      guide: '#ffedd5',
      dialog: '#fff4df',
    },
    creature: {
      id: 'orchard-bird',
      type: 'bird',
      name: 'Orchard Bird',
      greeting: 'Hola, sweet traveler!',
      prompt: 'Would you gather 3 sun drops so the orchard can ripen its fruit?',
      reminder: 'The sun drops are glowing above the citrus arches.',
      thanks: 'The orchard is singing again. Take some joyful sparkle with you.',
    },
    quest: {
      id: 'sun-drops',
      itemType: 'sun-drop',
      itemLabel: 'sun drop',
      itemPlural: 'sun drops',
      total: 3,
      hudLabel: 'Sun Drops',
      collectVerb: 'Gather',
      completionText: 'Golden fruit-lights ripple through the orchard rows.',
    },
    companion: {
      id: 'songbird',
      name: 'Songbird',
      ability: 'Adds a little extra harmony to every collected treasure.',
      effect: 'joy-chime',
      badge: 'SB',
    },
    landing: {
      platformName: 'Sun Landing',
      gateName: 'Golden Arbor',
      landmarkName: 'Citrus Arbor',
      layout: createLandingLayout(0.16, 0.42, 0.64, 0.85, 0.32),
      landmarkDialog: [
        'Warm fruit lights sway low',
        'small bird songs wake the bright path',
        'gold arches glow ahead',
      ],
      village: createVillageSpot(
        'Mirror Stand',
        'mirror-stand',
        [
          'Bright glass waits here',
          'sun paths sharpen in warm gold',
          'the arbor shines east',
        ],
        0.76,
        0.88,
        {
          label: 'Sun Path',
          prompt: 'Catch the light',
          activePrompt: 'Sun path is bright',
          reactionType: 'sun-path',
          mechanicType: 'mirror-guide',
          mechanicLabel: 'Mirror Guide',
          mechanicHint: 'Light marks the route',
          activationLines: [
            'Mirror light runs gold',
            'a sun path points to the arbor',
            'you gained 8 harmony',
          ],
          revisitLines: [
            'The sun path stays bright',
            'warm gold points across the rows',
            'the arbor waits east',
          ],
        }
      ),
    },
    decorations: {
      far: [
        createDecoration('hill', 0.04, 0.2, 0.38, 0.18, 'rgba(196, 124, 56, 0.14)'),
        createDecoration('hill', 0.58, 0.18, 0.3, 0.16, 'rgba(255, 188, 112, 0.18)'),
        createDecoration('sun', 0.74, 0.11, 0.1, 0.13, 'rgba(255, 213, 131, 0.32)'),
      ],
      mid: [
        createDecoration('arch', 0.16, 0.36, 0.16, 0.12, 'rgba(190, 121, 64, 0.36)'),
        createDecoration('fruit-cluster', 0.68, 0.34, 0.16, 0.12, 'rgba(255, 170, 78, 0.56)'),
        createDecoration('bird', 0.46, 0.28, 0.12, 0.07, 'rgba(255, 250, 240, 0.44)'),
      ],
      fore: [
        createDecoration('grass', 0.12, 0.8, 0.2, 0.12, 'rgba(157, 185, 89, 0.28)'),
        createDecoration('fruit-cluster', 0.24, 0.7, 0.12, 0.1, 'rgba(255, 182, 95, 0.44)', {
          small: true,
        }),
        createDecoration('grass', 0.72, 0.82, 0.2, 0.12, 'rgba(157, 185, 89, 0.28)'),
      ],
    },
  },
  {
    id: 'bluebonnet-prairie',
    name: 'Bluebonnet Prairie',
    shortName: 'Bluebonnet Prairie',
    regionLabel: 'Open sky, fireflies, bluebonnets, and prairie windmills',
    goalLabel: 'Prairie Gate',
    palette: {
      skyTint: 'linear-gradient(180deg, rgba(76, 147, 224, 0.12) 0%, rgba(195, 229, 255, 0.24) 100%)',
      horizonGlow: 'rgba(215, 236, 255, 0.24)',
      panel: 'rgba(245, 251, 255, 0.82)',
      panelBorder: 'rgba(255, 255, 255, 0.72)',
      heading: '#17345c',
      accent: '#2563eb',
      accentSoft: '#dbeafe',
      questItem: '#60a5fa',
      questItemSoft: '#eff6ff',
      platformTint: 'rgba(84, 147, 220, 0.2)',
      platformGlow: 'rgba(96, 165, 250, 0.34)',
      leaf: '#c5f0c9',
      leafShadow: 'rgba(31, 74, 44, 0.18)',
      creature: '#e5ecf9',
      creatureSecondary: '#fffefb',
      companion: '#93c5fd',
      guide: '#dbeafe',
      dialog: '#eef6ff',
    },
    creature: {
      id: 'prairie-armadillo',
      type: 'armadillo',
      name: 'Prairie Armadillo',
      greeting: 'Howdy, little helper!',
      prompt: 'Could you round up 3 lost fireflies for tonight\'s prairie dance?',
      reminder: 'The fireflies drift near the bluebonnet platforms.',
      thanks: 'The fireflies are home. Your path will feel easier now.',
    },
    quest: {
      id: 'lost-fireflies',
      itemType: 'firefly',
      itemLabel: 'firefly',
      itemPlural: 'fireflies',
      total: 3,
      hudLabel: 'Fireflies Home',
      collectVerb: 'Find',
      completionText: 'Bluebonnets shimmer as the fireflies settle in.',
    },
    companion: {
      id: 'firefly-friend',
      name: 'Firefly Friend',
      ability: 'Gently pulls nearby treasures toward the unicorn.',
      effect: 'firefly-magnet',
      badge: 'FF',
    },
    landing: {
      platformName: 'Prairie Landing',
      gateName: 'Windmill Gate',
      landmarkName: 'Bluebonnet Patch',
      layout: createLandingLayout(0.2, 0.72, 0.5, 0.87, 0.34),
      landmarkDialog: [
        'Bluebonnets nod bright',
        'firefly wishes wake with care',
        'the prairie gate will spin',
      ],
      village: createVillageSpot(
        'Windmill Post',
        'windmill-post',
        [
          'Small blades turn here',
          'field folk watch the tall grass bend',
          'the gate spins upwind',
        ],
        0.82,
        1.02,
        {
          label: 'Prairie Spin',
          prompt: 'Spin the blades',
          activePrompt: 'Wind is awake',
          reactionType: 'prairie-spin',
          mechanicType: 'wind-lift',
          mechanicLabel: 'Wind Lift',
          mechanicHint: 'Updraft jumps rise higher',
          activationLines: [
            'Small blades spin fast',
            'prairie lights dance toward the gate',
            'you gained 8 harmony',
          ],
          revisitLines: [
            'The prairie spins on',
            'blue lights drift across the grass',
            'the gate waits upwind',
          ],
        }
      ),
    },
    decorations: {
      far: [
        createDecoration('hill', 0.06, 0.22, 0.32, 0.16, 'rgba(111, 147, 177, 0.16)'),
        createDecoration('hill', 0.54, 0.2, 0.36, 0.16, 'rgba(100, 149, 203, 0.16)'),
        createDecoration('orb', 0.18, 0.13, 0.08, 0.1, 'rgba(255, 255, 255, 0.2)'),
      ],
      mid: [
        createDecoration('windmill', 0.72, 0.3, 0.12, 0.2, 'rgba(232, 241, 252, 0.46)'),
        createDecoration('sparkle-cluster', 0.44, 0.3, 0.14, 0.08, 'rgba(174, 222, 255, 0.54)'),
        createDecoration('flower-cluster', 0.16, 0.42, 0.16, 0.14, 'rgba(126, 170, 255, 0.48)'),
      ],
      fore: [
        createDecoration('grass', 0.12, 0.82, 0.22, 0.12, 'rgba(112, 158, 116, 0.28)'),
        createDecoration('flower-cluster', 0.28, 0.74, 0.12, 0.1, 'rgba(125, 174, 255, 0.46)', {
          small: true,
        }),
        createDecoration('grass', 0.72, 0.8, 0.22, 0.12, 'rgba(112, 158, 116, 0.28)'),
      ],
    },
  },
];

export const getBiomeConfig = (index) =>
  BIOMES[Math.min(BIOMES.length - 1, Math.max(0, index))];

export const getNextBiomeConfig = (index) =>
  index + 1 < BIOMES.length ? BIOMES[index + 1] : null;

const expandLayer = (entries, viewport, levelHeight, layerId) =>
  entries.map((entry, index) => ({
    ...entry,
    id: `${layerId}-${index}`,
    left: entry.x * viewport.width,
    top: entry.y * levelHeight,
    pixelWidth: entry.width * viewport.width,
    pixelHeight: entry.height * viewport.height,
  }));

export const buildBiomeDecorations = (biome, viewport, levelHeight) => ({
  far: expandLayer(biome.decorations.far, viewport, levelHeight, `${biome.id}-far`),
  mid: expandLayer(biome.decorations.mid, viewport, levelHeight, `${biome.id}-mid`),
  fore: expandLayer(biome.decorations.fore, viewport, levelHeight, `${biome.id}-fore`),
});
